<template>
  <b-modal
    :visible="value"
    size="lg"
    scrollable
    :title="modalTitle"
    ok-only
    ok-title="Close"
    modal-class="call-analysis-detail-modal"
    @change="$emit('input', $event)"
    @hidden="onHidden"
  >
    <div v-if="detailLoading && !detailAnalysis" class="text-center py-3">
      <b-spinner small variant="primary" />
      <p class="text-muted small mt-1 mb-0">Loading analysis…</p>
    </div>
    <b-alert
      v-else-if="detailError && !detailAnalysis"
      show
      variant="warning"
      class="mb-0"
    >
      {{ detailError }}
    </b-alert>
    <template v-else-if="call && detailAnalysis">
      <b-alert
        v-if="detailLoading"
        show
        variant="info"
        class="mb-1 p-1"
      >
        Loading full analysis…
      </b-alert>
      <b-alert
        v-if="detailError"
        show
        variant="warning"
        class="mb-1 p-1"
      >
        {{ detailError }}
      </b-alert>
      <div v-if="canPlayRecording" class="mb-1">
        <b-button
          v-if="!recording.url && !recording.loading && !recording.error"
          variant="outline-info"
          size="sm"
          @click="loadRecordingUrl"
        >
          <feather-icon icon="PlayCircleIcon" size="14" class="mr-50" />
          Load recording
        </b-button>
        <div v-if="recording.loading" class="text-muted small">
          <b-spinner small class="mr-50" />
          Loading recording…
        </div>
        <div v-else-if="recording.error" class="d-flex flex-wrap align-items-center text-danger small" style="gap:8px;">
          <span>
            <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
            {{ recording.error }}
          </span>
          <b-button variant="link" size="sm" class="p-0" @click="loadRecordingUrl">
            Try again
          </b-button>
        </div>
        <audio
          v-else-if="isPlayableRecordingUrl(recording.url)"
          :key="recordingIdentity || recording.url"
          controls
          preload="metadata"
          style="width:100%; outline:none;"
          :src="recording.url"
        >
          Your browser does not support audio playback.
        </audio>
      </div>

      <!-- Interest vs ATS status mismatch -->
      <b-alert
        v-if="hasInterestStatusMismatch(call)"
        show
        variant="danger"
        class="mb-1 p-1"
      >
        <div class="d-flex align-items-start">
          <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 flex-shrink-0 mt-25" />
          <div class="call-analysis-text-wrap min-width-0">
            <strong>Status mismatch</strong>
            <p class="mb-0 small mt-25">{{ getInterestMismatchReason(call) }}</p>
            <p class="mb-0 small text-muted mt-25">
              Recruiter moved this candidate to
              <strong>{{ getCandidateStatusFromRow(call) }}</strong>
              despite AI analysis indicating low or no interest.
            </p>
          </div>
        </div>
      </b-alert>

      <!-- Overall Score Hero -->
      <div class="rounded border p-1 mb-1" style="background: linear-gradient(135deg, #f8f8f8 0%, #fff 100%);">
        <b-row class="align-items-center">
          <b-col cols="12" md="4" class="text-center mb-1 mb-md-0">
            <small class="text-muted d-block mb-25">Overall Hygiene Score</small>
            <div
              class="font-weight-bold display-4 mb-50 text-primary"
            >{{ formatScorePercent(getHygieneScore(detailAnalysis)) }}</div>
            <div style="background:#ebe9f1; border-radius:6px; height:10px; overflow:hidden; max-width:180px; margin:0 auto;">
              <div
                :style="{
                  width: (getHygieneScore(detailAnalysis) != null ? Math.round(getHygieneScore(detailAnalysis) * 100) : 0) + '%',
                  height: '10px',
                  borderRadius: '6px',
                  background: hygieneScoreColor(getHygieneScore(detailAnalysis)),
                }"
              />
            </div>
          </b-col>
          <b-col cols="12" md="8" class="min-width-0">
            <b-row>
              <b-col cols="6" sm="3" class="mb-50">
                <small class="text-muted d-block">Call Type</small>
                <b-badge
                  v-if="detailCallTypeTag"
                  :variant="detailCallTypeTag.variant"
                >{{ detailCallTypeTag.label }}</b-badge>
                <span v-else>—</span>
              </b-col>
              <b-col cols="6" sm="3" class="mb-50">
                <small class="text-muted d-block">Outcome</small>
                <b-badge :variant="outcomeBadgeVariant(detailCallOutcomeCategory)" class="text-capitalize">
                  {{ formatOutcomeLabel(detailAnalysis) }}
                </b-badge>
              </b-col>
              <b-col cols="6" sm="3" class="mb-50">
                <small class="text-muted d-block">Interest</small>
                <b-badge :variant="interestBadgeVariant(detailCandidateInterestLevel)" class="text-capitalize">
                  {{ formatInterestLabel(detailAnalysis) }}
                </b-badge>
              </b-col>
              <b-col cols="6" sm="3" class="mb-50">
                <small class="text-muted d-block">Engagement</small>
                <b-badge
                  v-if="detailCallQualitySummary && detailCallQualitySummary.candidate_engagement"
                  :variant="engagementBadgeVariant(detailCallQualitySummary.candidate_engagement)"
                  class="text-capitalize"
                >{{ formatQualityLevel(detailCallQualitySummary.candidate_engagement) }}</b-badge>
                <span v-else>—</span>
              </b-col>
              <b-col cols="6" sm="3" class="mb-50">
                <small class="text-muted d-block">Professionalism</small>
                <b-badge
                  v-if="detailCallQualitySummary && detailCallQualitySummary.recruiter_professionalism"
                  :variant="professionalismBadgeVariant(detailCallQualitySummary.recruiter_professionalism)"
                  class="text-capitalize"
                >{{ formatQualityLevel(detailCallQualitySummary.recruiter_professionalism) }}</b-badge>
                <span v-else>—</span>
              </b-col>
            </b-row>
            <div v-if="getRedFlags(detailAnalysis).length" class="mt-50 call-analysis-red-flags-wrap">
              <small class="text-muted d-block mb-25">Red Flags ({{ getRedFlags(detailAnalysis).length }})</small>
              <div class="call-analysis-red-flags">
                <b-badge
                  v-for="(flag, idx) in getRedFlags(detailAnalysis)"
                  :key="'hero-rf-'+idx"
                  variant="light-danger"
                  class="call-analysis-red-flag-badge mb-25"
                >{{ flag }}</b-badge>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- My view: what was missed -->
      <b-alert
        v-if="viewMode === 'my' && missedChecksForDetail.length"
        show
        variant="warning"
        class="mb-1 p-1"
      >
        <div class="d-flex align-items-center mb-50">
          <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
          <strong>Areas to improve on this call</strong>
        </div>
        <ul class="mb-0 pl-1 small call-analysis-text-wrap">
          <li v-for="check in missedChecksForDetail" :key="check.key">
            <strong>{{ check.label }}</strong>
            <span v-if="check.notes"> — {{ check.notes }}</span>
          </li>
        </ul>
      </b-alert>

      <!-- Call Summary -->
      <b-alert show variant="light" class="mb-1 p-1">
        <div class="d-flex align-items-center mb-50">
          <feather-icon icon="MessageSquareIcon" size="14" class="mr-50 text-primary" />
          <strong>Call Summary</strong>
        </div>
        <p class="mb-0 small call-analysis-text-wrap">
          <template v-if="detailAnalysis.call_summary">{{ detailAnalysis.call_summary }}</template>
          <span v-else-if="detailLoading" class="text-muted">Loading summary…</span>
          <span v-else class="text-muted">No summary available.</span>
        </p>
      </b-alert>

      <!-- Interest & Outcome -->
      <b-row class="mb-1">
        <b-col cols="12" md="6" class="mb-1 mb-md-0">
          <b-card no-body class="h-100 border">
            <b-card-header class="py-50 px-1">
              <small class="font-weight-bold text-muted">Candidate Interest</small>
            </b-card-header>
            <b-card-body class="p-1">
              <b-badge :variant="interestBadgeVariant(detailCandidateInterestLevel)" class="mb-50">
                {{ formatInterestLabel(detailAnalysis) }}
              </b-badge>
              <div v-if="detailCandidateInterest && detailCandidateInterest.interested != null" class="small mb-25">
                Interested: <strong>{{ detailCandidateInterest.interested ? 'Yes' : 'No' }}</strong>
              </div>
              <div v-if="detailCandidateInterest && detailCandidateInterest.confidence != null" class="small text-muted">
                Confidence: {{ formatScorePercent(detailCandidateInterest.confidence, 1) }}
              </div>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card no-body class="h-100 border">
            <b-card-header class="py-50 px-1">
              <small class="font-weight-bold text-muted">Call Outcome</small>
            </b-card-header>
            <b-card-body class="p-1">
              <b-badge :variant="outcomeBadgeVariant(detailCallOutcomeCategory)" class="mb-50">
                {{ formatOutcomeLabel(detailAnalysis) }}
              </b-badge>
              <p v-if="detailCallOutcome && detailCallOutcome.evidence" class="mb-25 small call-analysis-text-wrap">{{ detailCallOutcome.evidence }}</p>
              <div v-if="detailCallOutcome && detailCallOutcome.confidence != null" class="small text-muted">
                Confidence: {{ formatScorePercent(detailCallOutcome.confidence, 1) }}
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Hygiene Checks -->
      <b-card no-body class="mb-1 border">
        <b-card-header class="d-flex align-items-center py-50 px-1">
          <feather-icon icon="CheckSquareIcon" size="14" class="mr-50 text-primary" />
          <small class="font-weight-bold">Hygiene Checks</small>
          <b-badge
            :variant="hygieneScoreVariant(getHygieneScore(detailAnalysis))"
            class="ml-auto"
          >Score: {{ formatScorePercent(getHygieneScore(detailAnalysis)) }}</b-badge>
        </b-card-header>
        <b-card-body class="p-1">
          <div
            v-for="check in listHygieneChecks(detailAnalysis)"
            :key="check.key"
            class="d-flex align-items-start py-50 border-bottom"
            style="gap:8px;"
          >
            <b-badge
              :variant="check.done === true ? 'light-primary' : 'light-secondary'"
              class="flex-shrink-0 mt-25"
            >
              {{ check.done === true ? 'Done' : check.done === false ? 'Not Done' : '—' }}
            </b-badge>
            <div class="flex-grow-1 min-width-0 call-analysis-text-wrap">
              <div class="font-weight-bold small">{{ check.label }}</div>
              <small v-if="check.notes" class="text-muted d-block">{{ check.notes }}</small>
              <small v-if="check.confidence != null" class="text-muted">Confidence: {{ formatScorePercent(check.confidence, 1) }}</small>
            </div>
          </div>
          <div v-if="!listHygieneChecks(detailAnalysis).length" class="text-muted small py-1">
            {{ detailLoading ? 'Loading hygiene checks…' : 'No hygiene check details available.' }}
          </div>
        </b-card-body>
      </b-card>

      <!-- Captured Details -->
      <b-card v-if="capturedDetailEntries.length" no-body class="mb-1 border">
        <b-card-header class="py-50 px-1">
          <feather-icon icon="ClipboardIcon" size="14" class="mr-50 text-info" />
          <small class="font-weight-bold">Captured Details</small>
        </b-card-header>
        <b-card-body class="p-1">
          <b-row>
            <b-col
              v-for="entry in capturedDetailEntries"
              :key="entry.key"
              cols="12"
              sm="6"
              class="mb-50"
            >
              <small class="text-muted d-block">{{ entry.label }}</small>
              <span class="small font-weight-bold">{{ entry.value }}</span>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Call Quality -->
      <b-card v-if="detailAnalysis.call_quality" no-body class="mb-1 border">
        <b-card-header class="py-50 px-1">
          <feather-icon icon="AwardIcon" size="14" class="mr-50 text-warning" />
          <small class="font-weight-bold">Call Quality</small>
        </b-card-header>
        <b-card-body class="p-1">
          <b-row>
            <b-col cols="6" sm="4" class="mb-50">
              <small class="text-muted d-block">Engagement</small>
              <b-badge
                v-if="detailAnalysis.call_quality.candidate_engagement"
                :variant="engagementBadgeVariant(detailAnalysis.call_quality.candidate_engagement)"
                class="text-capitalize"
              >{{ formatQualityLevel(detailAnalysis.call_quality.candidate_engagement) }}</b-badge>
              <span v-else class="small font-weight-bold">—</span>
            </b-col>
            <b-col cols="6" sm="4" class="mb-50">
              <small class="text-muted d-block">Professionalism</small>
              <b-badge
                v-if="detailAnalysis.call_quality.recruiter_professionalism"
                :variant="professionalismBadgeVariant(detailAnalysis.call_quality.recruiter_professionalism)"
                class="text-capitalize"
              >{{ formatQualityLevel(detailAnalysis.call_quality.recruiter_professionalism) }}</b-badge>
              <span v-else class="small font-weight-bold">—</span>
            </b-col>
            <b-col cols="12" sm="4" class="mb-50 call-analysis-red-flags-wrap">
              <small class="text-muted d-block">Red Flags</small>
              <div v-if="getRedFlags(detailAnalysis).length" class="call-analysis-red-flags mt-25">
                <b-badge
                  v-for="(flag, idx) in getRedFlags(detailAnalysis)"
                  :key="'rf-'+idx"
                  variant="light-danger"
                  class="call-analysis-red-flag-badge mb-25"
                >{{ flag }}</b-badge>
              </div>
              <span v-else class="small font-weight-bold text-muted">None</span>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Meta -->
      <div class="d-flex flex-wrap text-muted small" style="gap:12px;">
        <span v-if="detailAnalysis.analyzed_at || call._callAnalysisAt">
          <feather-icon icon="ClockIcon" size="11" class="mr-25" />
          Analyzed: {{ formatAnalyzedAt(detailAnalysis.analyzed_at || call._callAnalysisAt) }}
        </span>
        <span v-if="detailAnalysis.language_detected">
          <feather-icon icon="GlobeIcon" size="11" class="mr-25" />
          {{ detailAnalysis.language_detected }}
        </span>
        <span v-if="detailFollowUpLabel">
          <feather-icon icon="RepeatIcon" size="11" class="mr-25" />
          Follow-up: {{ detailFollowUpLabel }}
        </span>
        <span v-if="detailMetaProcessingTimeMs">
          Processed in {{ Math.round(detailMetaProcessingTimeMs / 1000) }}s
        </span>
      </div>
    </template>
    <p v-else class="text-muted text-center py-2 mb-0">
      {{ detailError || 'No analysis available for this call.' }}
    </p>
  </b-modal>
</template>

<script>
import {
  BModal,
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BBadge,
  BAlert,
  BSpinner,
  BButton,
} from 'bootstrap-vue'
import callAnalyticsService from '@/services/call-analytics-service'
import { hasRecordingAvailable, isValidRecordingUrl } from '@/utils/call-recording-helpers'
import {
  parseAnalysis,
  stripAnalysisTranscript,
  hygieneScoreColor,
  hygieneScoreVariant,
  interestBadgeVariant,
  outcomeBadgeVariant,
  engagementBadgeVariant,
  professionalismBadgeVariant,
  getHygieneScore,
  getRedFlags,
  getMissedHygieneChecks,
  formatScorePercent,
  listHygieneChecks,
  formatOutcomeLabel,
  formatInterestLabel,
  formatQualityLevel,
  getCallTypeTag,
  hasInterestStatusMismatch,
  getInterestMismatchReason,
  getCandidateStatusFromRow,
} from '@/utils/call-analysis-helpers'

const CAPTURED_DETAIL_LABELS = Object.freeze({
  current_location: 'Current Location',
  expected_ctc: 'Expected CTC',
  current_ctc: 'Current CTC',
  notice_period: 'Notice Period',
  skills_mentioned: 'Skills Mentioned',
  years_experience: 'Years of Experience',
  preferred_location: 'Preferred Location',
  current_company: 'Current Company',
  current_designation: 'Current Designation',
})

export default {
  name: 'CallAnalysisDetailModal',

  components: {
    BModal,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BAlert,
    BSpinner,
    BButton,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    call: {
      type: Object,
      default: null,
    },
    viewMode: {
      type: String,
      default: 'my',
    },
  },

  data() {
    // Vue 2 does not proxy data keys that start with _ or $. Using those
    // names made analysisLoadGen become NaN and discarded every API 2 apply.
    return {
      recording: {
        url: null,
        error: null,
        loading: false,
      },
      recordingLoadGen: 0,
      recordingIdentity: '',
      fetchedAnalysis: null,
      detailLoading: false,
      detailError: null,
      analysisLoadGen: 0,
      loadedIdentity: '',
      analysisWatchdog: null,
      recordingWatchdog: null,
    }
  },

  watch: {
    value(open) {
      if (open) this.onModalOpen()
    },
    call(next, prev) {
      if (!this.value) return
      // Same sid/id — ignore parent row-object churn so we do not reset loaders.
      if (this.callIdentity(next) && this.callIdentity(next) === this.callIdentity(prev)) return
      this.onModalOpen()
    },
  },

  computed: {
    detailAnalysis() {
      if (this.fetchedAnalysis) return stripAnalysisTranscript(this.fetchedAnalysis)
      // History slim badges while API 2 loads — do not wait on the spinner.
      const rowAnalysis = this.call ? parseAnalysis(this.call) : null
      return rowAnalysis ? stripAnalysisTranscript(rowAnalysis) : null
    },

    detailCallTypeTag() {
      return this.call ? getCallTypeTag(this.call) : null
    },

    detailCallOutcome() {
      return this.detailAnalysis && this.detailAnalysis.call_outcome
        ? this.detailAnalysis.call_outcome
        : null
    },

    detailCallOutcomeCategory() {
      return this.detailCallOutcome ? this.detailCallOutcome.category : null
    },

    detailCandidateInterest() {
      return this.detailAnalysis && this.detailAnalysis.candidate_interest
        ? this.detailAnalysis.candidate_interest
        : null
    },

    detailCandidateInterestLevel() {
      return this.detailCandidateInterest ? this.detailCandidateInterest.level : null
    },

    detailCallQualitySummary() {
      return this.detailAnalysis && this.detailAnalysis.call_quality
        ? this.detailAnalysis.call_quality
        : null
    },

    detailMetaProcessingTimeMs() {
      if (!this.detailAnalysis) return null
      if (this.detailAnalysis.processing_time_ms != null) return this.detailAnalysis.processing_time_ms
      return this.detailAnalysis.meta ? this.detailAnalysis.meta.processing_time_ms : null
    },

    detailFollowUpLabel() {
      if (!this.detailAnalysis) return ''
      const raw = this.detailAnalysis.is_follow_up_call != null
        ? this.detailAnalysis.is_follow_up_call
        : this.detailAnalysis.is_follow_up
      if (raw == null) return ''
      if (typeof raw === 'object') return raw.value ? 'Yes' : 'No'
      return raw ? 'Yes' : 'No'
    },

    missedChecksForDetail() {
      return this.detailAnalysis ? getMissedHygieneChecks(this.detailAnalysis) : []
    },

    modalTitle() {
      if (!this.call) return 'Call Analysis'
      const name = this.call.candidate && this.call.candidate !== '—' ? this.call.candidate : this.call.phone
      return this.viewMode === 'my' ? `Your Call — ${name}` : `Analysis — ${name}`
    },

    canPlayRecording() {
      return hasRecordingAvailable(this.call)
    },

    capturedDetailEntries() {
      const details = this.detailAnalysis?.captured_details
      if (!details || typeof details !== 'object') return []
      return Object.keys(details)
        .filter(key => details[key] != null && details[key] !== '')
        .map(key => ({
          key,
          label: CAPTURED_DETAIL_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()),
          value: this.formatCapturedValue(details[key]),
        }))
    },
  },

  methods: {
    hygieneScoreColor,
    hygieneScoreVariant,
    interestBadgeVariant,
    outcomeBadgeVariant,
    engagementBadgeVariant,
    professionalismBadgeVariant,
    getHygieneScore,
    getRedFlags,
    formatScorePercent,
    listHygieneChecks,
    formatOutcomeLabel,
    formatInterestLabel,
    formatQualityLevel,
    hasInterestStatusMismatch,
    getInterestMismatchReason,
    getCandidateStatusFromRow,

    formatCapturedValue(val) {
      if (val == null || val === '') return '—'
      if (Array.isArray(val)) return val.length ? val.join(', ') : '—'
      if (typeof val === 'object') return JSON.stringify(val)
      return String(val)
    },

    formatAnalyzedAt(raw) {
      if (!raw) return '—'
      try {
        return new Date(raw).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })
      } catch {
        return String(raw)
      }
    },

    isPlayableRecordingUrl(url) {
      return isValidRecordingUrl(url)
    },

    callIdentity(call) {
      if (!call) return ''
      const sid = String(call.sid || call.CallSid || call.call_sid || '').trim()
      if (sid) return `s:${sid}`
      const id = call.callDbId != null && call.callDbId !== ''
        ? call.callDbId
        : call.id
      return id != null && id !== '' ? `id:${id}` : ''
    },

    pickCallIds(call) {
      if (!call) return { sid: '', callDbId: '' }
      const sid = String(call.sid || call.CallSid || call.call_sid || '').trim()
      const rawId = call.callDbId != null && call.callDbId !== ''
        ? call.callDbId
        : call.id
      return {
        sid,
        callDbId: rawId != null && rawId !== '' ? rawId : '',
      }
    },

    clearAnalysisWatchdog() {
      if (this.analysisWatchdog) {
        clearTimeout(this.analysisWatchdog)
        this.analysisWatchdog = null
      }
    },

    clearRecordingWatchdog() {
      if (this.recordingWatchdog) {
        clearTimeout(this.recordingWatchdog)
        this.recordingWatchdog = null
      }
    },

    resetRecording() {
      this.recordingLoadGen += 1
      this.clearRecordingWatchdog()
      this.recordingIdentity = ''
      this.recording = { url: null, error: null, loading: false }
    },

    resetAnalysisDetail() {
      this.analysisLoadGen += 1
      this.clearAnalysisWatchdog()
      this.fetchedAnalysis = null
      this.loadedIdentity = ''
      this.detailLoading = false
      this.detailError = null
    },

    onModalOpen() {
      const identity = this.callIdentity(this.call)
      if (identity && identity === this.loadedIdentity && this.fetchedAnalysis) {
        this.detailLoading = false
      } else {
        this.loadAnalysisDetail()
      }
      if (identity && identity === this.recordingIdentity && (this.recording.url || this.recording.error)) {
        this.recording.loading = false
        return
      }
      this.resetRecording()
      // Prefetch URL only — never start playback on open.
      if (this.canPlayRecording) this.loadRecordingUrl()
    },

    toastAnalysisUnavailable(message) {
      if (!this.$bvToast || typeof this.$bvToast.toast !== 'function') return
      this.$bvToast.toast(message || 'Analysis is slow or unavailable.', {
        title: 'Call Analysis',
        variant: 'warning',
        solid: true,
        autoHideDelay: 6000,
      })
    },

    /**
     * API 2 — GET /api/exotel/call-analytics/:sid/analysis.
     * Called only when the modal opens (View Analysis / row click).
     * Slim History badges paint immediately; API 2 is the only full-analysis fetch.
     */
    async loadAnalysisDetail() {
      const call = this.call
      if (!call) {
        this.fetchedAnalysis = null
        this.loadedIdentity = ''
        this.detailLoading = false
        this.detailError = null
        return
      }
      const { sid, callDbId } = this.pickCallIds(call)
      const identity = this.callIdentity(call)
      if (identity && identity === this.loadedIdentity && this.fetchedAnalysis) {
        this.detailLoading = false
        this.detailError = null
        return
      }
      const gen = (this.analysisLoadGen += 1)
      this.clearAnalysisWatchdog()
      this.detailError = null
      if (identity !== this.loadedIdentity) this.fetchedAnalysis = null
      this.detailLoading = true
      this.analysisWatchdog = setTimeout(() => {
        if (gen !== this.analysisLoadGen) return
        this.detailLoading = false
        if (!this.fetchedAnalysis) {
          this.detailError = 'Analysis is taking too long. Please try again.'
          this.toastAnalysisUnavailable(this.detailError)
        }
      }, 12000)
      try {
        const res = await callAnalyticsService.getCallAnalysisDetail({
          sid,
          callDbId,
        })
        if (gen !== this.analysisLoadGen) return
        if (res && res.status === 'OK' && res.data) {
          this.fetchedAnalysis = res.data
          this.loadedIdentity = identity
          this.detailError = null
          return
        }
        if (res && res.status === 'CANCELLED') {
          this.detailError = this.detailError || 'Analysis request was cancelled. Please try again.'
          return
        }
        const rowPreview = parseAnalysis(call)
        const message = (res && res.message) || 'Analysis not available for this call.'
        this.fetchedAnalysis = null
        if (res && res.status === 'TIMEOUT') {
          this.detailError = rowPreview ? message : 'Analysis not available for this call.'
          this.toastAnalysisUnavailable(message)
          return
        }
        if (res && res.status === 'UNAVAILABLE') {
          this.detailError = rowPreview ? null : 'Analysis not available for this call.'
          if (!rowPreview) this.toastAnalysisUnavailable('Analysis not available for this call.')
          return
        }
        this.detailError = message
        if (!rowPreview) this.toastAnalysisUnavailable(message)
      } catch (err) {
        if (gen !== this.analysisLoadGen) return
        this.fetchedAnalysis = null
        this.detailError = 'Analysis not available for this call.'
        this.toastAnalysisUnavailable(this.detailError)
      } finally {
        this.clearAnalysisWatchdog()
        if (gen === this.analysisLoadGen) this.detailLoading = false
      }
    },

    /** Resolve the play URL so native controls can appear. Does not call play(). */
    async loadRecordingUrl() {
      const call = this.call
      if (!call || !hasRecordingAvailable(call)) return
      const identity = this.callIdentity(call)
      if (identity && identity === this.recordingIdentity && isValidRecordingUrl(this.recording.url)) {
        this.recording.loading = false
        return
      }
      this.recording.loading = true
      this.recording.url = null
      this.recording.error = null
      const gen = (this.recordingLoadGen += 1)
      this.clearRecordingWatchdog()
      this.recordingWatchdog = setTimeout(() => {
        if (gen !== this.recordingLoadGen) return
        this.recording.loading = false
        if (!this.recording.url) {
          this.recording.error = 'Recording is taking too long. Please try again.'
        }
      }, 15000)
      try {
        const ids = this.pickCallIds(call)
        const res = await callAnalyticsService.resolveRecordingPlayUrl({
          recordingS3Key: call.recordingS3Key || call.recording_s3_key || null,
          sid: ids.sid || null,
          callDbId: ids.callDbId || null,
          item: call,
        })
        if (gen !== this.recordingLoadGen) return
        if (res?.status === 'OK' && isValidRecordingUrl(res.url)) {
          this.recording.url = res.url
          this.recordingIdentity = identity
          return
        }
        this.recording.url = null
        this.recording.error = res?.message || 'No recording available for this call.'
        this.recordingIdentity = identity
      } catch (err) {
        if (gen !== this.recordingLoadGen) return
        this.recording.url = null
        this.recording.error = 'Could not load recording. Please try again.'
        this.recordingIdentity = identity
      } finally {
        this.clearRecordingWatchdog()
        if (gen === this.recordingLoadGen) this.recording.loading = false
      }
    },

    onHidden() {
      this.resetRecording()
      this.resetAnalysisDetail()
      this.$emit('hidden')
    },
  },

  beforeDestroy() {
    this.clearAnalysisWatchdog()
    this.clearRecordingWatchdog()
  },
}
</script>

<style lang="scss" scoped>
.min-width-0 {
  min-width: 0;
}

.call-analysis-text-wrap {
  overflow-wrap: break-word;
  word-break: break-word;
}

.call-analysis-red-flags-wrap {
  min-width: 0;
  max-width: 100%;
}

.call-analysis-red-flags {
  min-width: 0;
  max-width: 100%;
}

.call-analysis-red-flag-badge {
  display: block;
  width: 100%;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: left;
  height: auto;
  line-height: 1.4;
}
</style>

<style>
/* Teleported to body — sit above the Flags modal when stacked. */
.call-analysis-detail-modal {
  z-index: 1100;
}
</style>
